/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all firmware version artifacts from the API
 */
export const fetchAllFirmwareVersions = createAsyncThunk(
  'firmwareVersion/fetchAllFirmwareVersions',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchFirmwareArtifacts(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch firmware versions',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

/**
 * Placeholder filter-facet thunk. There is no backend `/firmware/artifacts/filters`
 * endpoint, so this resolves immediately with no extra facets — it exists only so
 * CustomTable's v2 header (search bar, refresh button, upload button via
 * rightTopContent) mounts, since CustomTable only renders that header when
 * `filters && filterListToDispatch` is truthy.
 */
export const fetchFirmwareVersionFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'firmwareVersion/fetchFirmwareVersionFilters',
  async () => {
    return [];
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for FirmwareVersion slice
 */
const initialState = {
  firmwareVersions: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 20,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

const firmwareVersionSlice = createSlice({
  name: 'firmwareVersion',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setFirmwareVersionPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setFirmwareVersionLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateFirmwareVersionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableFirmwareVersionFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setFirmwareVersionSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setFirmwareVersionSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllFirmwareVersions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllFirmwareVersions.fulfilled, (state, action) => {
        state.loading = false;
        state.firmwareVersions = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllFirmwareVersions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchFirmwareVersionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchFirmwareVersionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          if (action.payload?.length > 0) {
            state.filters = [...(state.filters || []), ...action.payload];
          }
        },
      )
      .addCase(fetchFirmwareVersionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setFirmwareVersionPage,
  setFirmwareVersionLimit,
  updateFirmwareVersionFilters,
  disableFirmwareVersionFilterApplied,
  setFirmwareVersionSortBy,
  setFirmwareVersionSortField,
} = firmwareVersionSlice.actions;

export default firmwareVersionSlice.reducer;
